import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Mail, Menu, Phone, X, ShieldCheck } from "lucide-react";
import { api } from "../api";

export function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <span className="w-10 h-10 bg-amber text-ink font-display font-black text-2xl leading-none flex items-center justify-center transition-transform duration-300 group-hover:rotate-90">
        X
      </span>
      <span className="font-display font-extrabold tracking-[0.18em] text-sm leading-tight text-white">
        XTREME
        <br />
        <span className="text-amber">PROTECTION</span>
      </span>
    </Link>
  );
}

const NAV_ITEMS = [
  { to: "/services", label: "Services", testId: "nav-services-link" },
  { to: "/locations", label: "UK Locations", testId: "nav-locations-link" },
  { to: "/insights", label: "Insights", testId: "nav-insights-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="h-0.5 hazard" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:text-amber ${
                  isActive ? "text-amber" : "text-white/70"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <span className="hidden lg:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-400/30 px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            24/7 UK Command
          </span>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <div className="bg-amber text-ink" data-testid="contact-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-9 flex items-center justify-center sm:justify-end gap-5">
          <a
            href="tel:+442046402840"
            data-testid="strip-phone-link"
            className="flex items-center gap-1.5 font-mono text-[10px] sm:text-xs font-semibold tracking-wider hover:opacity-70 transition-opacity"
          >
            <Phone className="w-3 h-3" /> +44 2046 402840
          </a>
          <a
            href="mailto:admin@xtremeprotection.uk"
            data-testid="strip-email-link"
            className="flex items-center gap-1.5 font-mono text-[10px] sm:text-xs font-semibold tracking-wider hover:opacity-70 transition-opacity"
          >
            <Mail className="w-3 h-3" /> admin@xtremeprotection.uk
          </a>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-ink/95 backdrop-blur-xl border-t border-white/10 px-5 py-6 space-y-4" data-testid="mobile-menu">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`mobile-${item.testId}`}
              className="block font-display font-bold uppercase tracking-widest text-2xl text-white hover:text-amber transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const [cities, setCities] = useState(null);
  useEffect(() => {
    api.cities().then(setCities).catch(() => {});
  }, []);

  return (
    <footer className="border-t border-white/10 bg-coal/50" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-6 text-sm text-gray-400 leading-relaxed max-w-sm">
              SIA-licensed security guard services across the United Kingdom. Manned guarding, mobile patrols, CCTV
              monitoring, door supervision, event security, key holding, close protection and K9 units — delivered in
              210+ towns and cities with verified reporting on every shift.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["SIA Licensed", "BS 7858 Vetted", "ISO 9001", "24/7 UK Command"].map((b) => (
                <span key={b} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5">
                  <ShieldCheck className="w-3 h-3" /> {b}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8">
            <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Security Services UK</h3>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mb-10" data-testid="footer-service-links">
              {[
                ["manned-guarding", "Manned Guarding"], ["door-supervision", "Door Supervision"],
                ["cctv-monitoring", "CCTV Monitoring"], ["mobile-security-patrols", "Mobile Patrols"],
                ["event-security", "Event Security"], ["construction-site-security", "Construction Site Security"],
                ["retail-security", "Retail Security"], ["corporate-security", "Corporate Security"],
                ["concierge-security", "Concierge Security"], ["key-holding-alarm-response", "Key Holding & Alarm Response"],
                ["close-protection", "Close Protection"], ["warehouse-industrial-security", "Warehouse Security"],
                ["k9-security-dog-handlers", "K9 Dog Handlers"], ["vacant-property-security", "Vacant Property Security"],
                ["hotel-security", "Hotel Security"], ["healthcare-security", "Healthcare Security"],
              ].map(([slug, name]) => (
                <Link key={slug} to={`/services/${slug}`} className="text-sm text-gray-400 hover:text-amber transition-colors">
                  {name}
                </Link>
              ))}
            </div>
            <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Security by Sector</h3>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mb-10" data-testid="footer-sector-links">
              {[
                ["construction", "Construction Security"], ["retail", "Retail Security"],
                ["logistics", "Logistics Security"], ["hospitality", "Hospitality Security"],
                ["corporate", "Corporate Security"], ["healthcare", "Healthcare Security"],
                ["education", "Education Security"], ["property", "Property Security"],
                ["events", "Event Security"],
              ].map(([slug, name]) => (
                <Link key={slug} to={`/sectors/${slug}`} className="text-sm text-gray-400 hover:text-amber transition-colors">
                  {name}
                </Link>
              ))}
            </div>
            {cities && (
              <>
                <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-5">Security Locations Across the UK</h3>
                <div className="space-y-5" data-testid="footer-location-links">
                  {cities.regions.map((g) => (
                    <div key={g.region}>
                      <Link
                        to={`/regions/${g.region.toLowerCase().replace(/\s+/g, "-")}`}
                        data-testid={`footer-region-${g.region.toLowerCase().replace(/\s+/g, "-")}`}
                        className="font-display font-bold uppercase tracking-widest text-amber text-sm hover:text-white transition-colors"
                      >
                        {g.region}
                      </Link>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
                        {g.cities.map((c) => (
                          <Link key={c.slug} to={`/locations/${c.slug}`} className="text-xs text-gray-500 hover:text-amber transition-colors">
                            Security {c.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600">
            © {new Date().getFullYear()} Xtreme Protection — UK Security Guard Services
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600">
            England · Scotland · Wales · Northern Ireland
          </p>
        </div>
      </div>
    </footer>
  );
}

export function StickyContactBar() {
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 border-t border-amber/40 bg-ink/90 backdrop-blur-xl"
      data-testid="sticky-contact-bar"
    >
      <div className="grid grid-cols-2">
        <a
          href="tel:+442046402840"
          data-testid="sticky-phone-btn"
          className="flex items-center justify-center gap-2 py-3.5 font-display font-bold uppercase tracking-widest text-sm sm:text-base text-amber hover:bg-amber hover:text-ink transition-colors duration-300"
        >
          <Phone className="w-4 h-4" /> +44 2046 402840
        </a>
        <a
          href="mailto:admin@xtremeprotection.uk"
          data-testid="sticky-email-btn"
          className="flex items-center justify-center gap-2 py-3.5 font-display font-bold uppercase tracking-widest text-sm sm:text-base text-amber border-l border-amber/40 hover:bg-amber hover:text-ink transition-colors duration-300"
        >
          <Mail className="w-4 h-4" /> <span className="hidden min-[420px]:inline">admin@xtremeprotection.uk</span><span className="min-[420px]:hidden">Email Us</span>
        </a>
      </div>
    </div>
  );
}

export function PageShell({ children }) {
  return (
    <div className="grain min-h-screen bg-ink text-foreground">
      <Nav />
      <main className="pt-[102px] sm:pt-[118px]">{children}</main>
      <Footer />
      <div className="h-14" aria-hidden="true" />
      <StickyContactBar />
    </div>
  );
}
